import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "@/App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { toast } from "sonner";
import {
  Users,
  Plus,
  Edit2,
  Key,
  Lock,
  Trash2,
  ChevronsUpDown,
  Check,
} from "lucide-react";
import { can } from "@/lib/permissions";
import { DiscardConfirm } from "@/components/DiscardConfirm";

const UserManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [savingUser, setSavingUser] = useState(false);
  const savingUserRef = useRef(false);
  const [branches, setBranches] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [activeTab, setActiveTab] = useState("active");
  const [supervisorPopoverOpen, setSupervisorPopoverOpen] = useState(false);
  // Username rename is a Settings-Master-only capability (backend enforces too).
  const isMaster = can(user, "settings", "master");
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [resetDiscardOpen, setResetDiscardOpen] = useState(false);
  const formSnapshotRef = useRef(null);

  // Snapshot the form when the Add/Edit dialog opens — dirty = form changed.
  useEffect(() => {
    if (dialogOpen) formSnapshotRef.current = JSON.stringify(formData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen]);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    name: "",
    role: "",
    mobile: "",
    department: "",
    designation: "",
    branch: "",
    supervisor: "",
    is_active: true,
  });

  useEffect(() => {
    // DP and CRM have admin access
    if (user?.role !== "CRM" && user?.role !== "DP" && user?.role !== "Admin") {
      toast.error("Access denied");
      navigate("/dashboard");
      return;
    }
    fetchUsers();
    fetchBranches();
    fetchRoles();
  }, [user, navigate]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API}/branches`, { withCredentials: true });
      setBranches(response.data || []);
    } catch (error) {
      console.error("Failed to load branches");
    }
  };

  // Roles now come from GET /api/roles (dynamic, defined on the Roles screen).
  // Previously the dropdown was a hardcoded CRE/Receptionist/CRM/DP list.
  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/roles`, { withCredentials: true });
      setAvailableRoles(response.data || []);
    } catch (error) {
      console.error("Failed to load roles");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (savingUserRef.current) return;

    // The primary `admin` account uses a stripped form (Name / Phone only), so
    // Department + Designation are not editable there and must not be demanded.
    const isAdminAccountForm = editingUser?.username === "admin";
    if (!formData.name) {
      toast.error("Please fill required fields (Name)");
      return;
    }
    if (!isAdminAccountForm && (!formData.role || !formData.department || !formData.designation)) {
      toast.error("Please fill required fields (Role, Department, Designation)");
      return;
    }

    // Supervisor is required for everyone EXCEPT:
    //  - the seeded root account (username === "admin"), or
    //  - anyone assigned the built-in "Admin" system role.
    // Create mode: exemption keys on the picked Role only (no editingUser yet).
    const isSeededAdmin = editingUser?.username === "admin";
    const isAdminRolePick = formData.role === "Admin";
    const supervisorRequired = !isSeededAdmin && !isAdminRolePick;
    if (supervisorRequired && !formData.supervisor) {
      toast.error("Select Supervisor");
      return;
    }

    if (!editingUser && (!formData.username || !formData.password)) {
      toast.error("Username and password are required for new users");
      return;
    }

    setSavingUser(true);
    savingUserRef.current = true;
    try {
      if (editingUser) {
        const updateData = {
          name: formData.name,
          role: formData.role,
          mobile: formData.mobile || null,
          department: formData.department,
          designation: formData.designation,
          branch: formData.branch || null,
          supervisor: formData.supervisor || "",
          is_active: formData.is_active,
        };
        if (isAdminAccountForm) {
          // The admin form only exposes Name + Phone. Role/Department/Designation
          // are read-only there, so they are not sent at all — echoing them back
          // could blank a stored value that the form never showed.
          delete updateData.role;
          delete updateData.department;
          delete updateData.designation;
          delete updateData.branch;
          delete updateData.supervisor;
        }
        if (isMaster && formData.username && formData.username !== editingUser.username) {
          updateData.username = formData.username;
        }
        await axios.put(`${API}/users/${editingUser.user_id}`, updateData, {
          withCredentials: true,
        });
        toast.success("User updated");
      } else {
        await axios.post(`${API}/users`, formData, { withCredentials: true });
        toast.success("User created");
      }
      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save user");
    } finally {
      setSavingUser(false);
      savingUserRef.current = false;
    }
  };

  const handleEdit = (u) => {
    setEditingUser(u);
    setFormData({
      username: u.username,
      password: "",
      name: u.name,
      role: u.role || "",
      mobile: u.mobile || "",
      department: u.department || "",
      designation: u.designation || "",
      branch: u.branch || "",
      supervisor: u.supervisor || "",
      is_active: u.is_active,
    });
    setDialogOpen(true);
  };

  const handleDeactivate = async () => {
    if (!window.confirm("Are you sure you want to deactivate this user?")) return;

    try {
      await axios.put(
        `${API}/users/${editingUser.user_id}`,
        { is_active: false },
        { withCredentials: true }
      );
      toast.success("User deactivated");
      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error("Failed to deactivate user");
    }
  };

  const handleToggleLock = async (userId, currentLockStatus) => {
    try {
      await axios.post(`${API}/users/${userId}/toggle-lock`, {}, { withCredentials: true });
      toast.success(currentLockStatus ? "User unlocked" : "User locked");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update lock status");
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword) {
      toast.error("Please enter a new password");
      return;
    }

    try {
      await axios.post(
        `${API}/users/${resetPasswordUser.user_id}/reset-password`,
        { password: newPassword },
        { withCredentials: true }
      );
      toast.success("Password reset successfully");
      setResetPasswordDialogOpen(false);
      setResetPasswordUser(null);
      setNewPassword("");
    } catch (error) {
      toast.error("Failed to reset password");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/users/${deletingUser.user_id}`, { withCredentials: true });
      toast.success("User permanently deleted");
      setDeleteDialogOpen(false);
      setDeletingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      username: "",
      password: "",
      name: "",
      role: "",
      mobile: "",
      department: "",
      designation: "",
      branch: "",
      supervisor: "",
      is_active: true,
    });
  };

  const filteredUsers = users.filter((u) =>
    activeTab === "active" ? u.is_active : !u.is_active
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-black text-white rounded-sm flex items-center justify-center">
            <Users className="w-6 h-6" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tighter uppercase">
              Users
            </h1>
            <p className="text-sm text-gray-500 mt-1">Manage team members and access</p>
          </div>
        </div>

        <Dialog
          open={dialogOpen}
          onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}
        >
          <DialogTrigger asChild>
            <Button
              className="bg-black text-white hover:bg-gray-800 rounded-sm text-xs uppercase tracking-wider"
              data-testid="add-user-btn"
            >
              <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent
            className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto"
            onInteractOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
            closeClassName="p-1 transition-colors hover:bg-red-600 hover:text-white hover:opacity-100"
            onCloseClick={(e) => {
              if (JSON.stringify(formData) !== formSnapshotRef.current) {
                e.preventDefault();
                setDiscardConfirmOpen(true);
              }
            }}
          >
            <DialogHeader>
              <DialogTitle className="font-heading font-bold text-xl tracking-tight uppercase">
                {editingUser ? "Edit User" : "Add User"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 pt-4">
              {(() => {
                // Admin-account guard: the seeded top user (username === "admin")
                // is not row-editable via the pencil, but the password-reset key
                // icon still opens this dialog with editingUser set. When that
                // happens, show a stripped form: Full Name, Phone Number, and a
                // disabled Username. All other fields (Department/Designation/
                // Supervisor/Role/Branch/Status + inline Password) are hidden.
                const isAdminAccount = editingUser?.username === "admin";
                if (isAdminAccount) {
                  return (
                    <div className="space-y-4" data-testid="user-form-admin">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
                            Full Name *
                          </Label>
                          <Input
                            value={formData.name}
                            onChange={(e) =>
                              setFormData({ ...formData, name: e.target.value })
                            }
                            placeholder="Full name"
                            className="rounded-sm"
                            data-testid="input-name"
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
                            Phone Number
                          </Label>
                          <Input
                            type="tel"
                            value={formData.mobile}
                            onChange={(e) =>
                              setFormData({ ...formData, mobile: e.target.value })
                            }
                            placeholder="Phone number"
                            className="rounded-sm"
                            data-testid="input-mobile"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
                            Username
                          </Label>
                          <Input
                            type="text"
                            value={formData.username}
                            className="rounded-sm"
                            disabled
                            data-testid="input-username"
                          />
                          <p className="text-[10px] text-gray-400 mt-1">
                            Username cannot be changed
                          </p>
                        </div>
                        <div>
                          <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
                            Role
                          </Label>
                          <Input
                            type="text"
                            value={formData.role}
                            className="rounded-sm"
                            disabled
                            readOnly
                            data-testid="input-role-readonly"
                          />
                          <p className="text-[10px] text-gray-400 mt-1">
                            Role cannot be changed
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                }

                // Supervisor requirement rule (kept local so the form stays declarative).
                const isSeededAdmin = editingUser?.username === "admin";
                const isAdminRolePick = formData.role === "Admin";
                const supervisorRequired = !isSeededAdmin && !isAdminRolePick;
                const supervisorName = formData.supervisor
                  ? users.find((x) => x.user_id === formData.supervisor)?.name
                  : "";
                const supervisorCandidates = users.filter(
                  (x) => x.user_id !== editingUser?.user_id && x.is_active !== false
                );

                return (
                  <>
                    {/* SECTION 1 — DETAILS */}
                    <div className="space-y-4" data-testid="user-form-details">
                      <h3 className="font-heading font-bold text-sm uppercase tracking-tight border-b border-gray-200 pb-2">
                        Details
                      </h3>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
                            Full Name *
                          </Label>
                          <Input
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="Full name"
                            className="rounded-sm"
                            data-testid="input-name"
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
                            Phone Number
                          </Label>
                          <Input
                            type="tel"
                            value={formData.mobile}
                            onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                            placeholder="Phone number"
                            className="rounded-sm"
                            data-testid="input-mobile"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
                            Department *
                          </Label>
                          <Input
                            value={formData.department}
                            onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                            placeholder="e.g. Customer Relations"
                            className="rounded-sm"
                            data-testid="input-department"
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
                            Designation *
                          </Label>
                          <Input
                            value={formData.designation}
                            onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                            placeholder="Job title"
                            className="rounded-sm"
                            data-testid="input-designation"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        {/* Supervisor — searchable combobox */}
                        <div>
                          <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
                            Supervisor {supervisorRequired ? "*" : ""}
                          </Label>
                          <Popover open={supervisorPopoverOpen} onOpenChange={setSupervisorPopoverOpen}>
                            <PopoverTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                role="combobox"
                                aria-expanded={supervisorPopoverOpen}
                                className="w-full justify-between rounded-sm font-normal"
                                data-testid="input-supervisor"
                              >
                                <span className={supervisorName ? "" : "text-gray-400"}>
                                  {supervisorName
                                    ? supervisorName
                                    : supervisorRequired
                                    ? "Select supervisor"
                                    : "None (Admin exempt)"}
                                </span>
                                <ChevronsUpDown className="ml-2 w-4 h-4 opacity-50 shrink-0" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              className="p-0 w-[--radix-popover-trigger-width]"
                              align="start"
                            >
                              <Command>
                                <CommandInput
                                  placeholder="Search users..."
                                  data-testid="supervisor-search"
                                />
                                <CommandList>
                                  <CommandEmpty>No users found.</CommandEmpty>
                                  <CommandGroup>
                                    {!supervisorRequired && (
                                      <CommandItem
                                        value="__none__"
                                        onSelect={() => {
                                          setFormData((f) => ({ ...f, supervisor: "" }));
                                          setSupervisorPopoverOpen(false);
                                        }}
                                        data-testid="supervisor-option-none"
                                      >
                                        <Check
                                          className={`mr-2 h-4 w-4 ${
                                            !formData.supervisor ? "opacity-100" : "opacity-0"
                                          }`}
                                        />
                                        <span className="flex-1 italic text-gray-500">None</span>
                                      </CommandItem>
                                    )}
                                    {supervisorCandidates.map((u) => (
                                      <CommandItem
                                        key={u.user_id}
                                        value={`${u.name} ${u.username}`}
                                        onSelect={() => {
                                          setFormData((f) => ({ ...f, supervisor: u.user_id }));
                                          setSupervisorPopoverOpen(false);
                                        }}
                                        data-testid={`supervisor-option-${u.user_id}`}
                                      >
                                        <Check
                                          className={`mr-2 h-4 w-4 ${
                                            formData.supervisor === u.user_id
                                              ? "opacity-100"
                                              : "opacity-0"
                                          }`}
                                        />
                                        <span className="flex-1">{u.name}</span>
                                        <span className="text-[10px] text-gray-400 ml-2">
                                          {u.role || "—"}
                                        </span>
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                          {!supervisorRequired && (
                            <p className="text-[10px] text-gray-400 mt-1">
                              Optional — Admin role / admin account is exempt.
                            </p>
                          )}
                        </div>

                        {/* Role */}
                        <div>
                          <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
                            Role *
                          </Label>
                          <Select
                            value={formData.role}
                            onValueChange={(v) => setFormData({ ...formData, role: v })}
                          >
                            <SelectTrigger className="rounded-sm" data-testid="input-role">
                              <SelectValue placeholder="Select a role" />
                            </SelectTrigger>
                            <SelectContent>
                              {(() => {
                                // 'Admin' is a system role: high permissions come from
                                // ticking Master on a normal role instead. Filter it out
                                // of the assignable list. It still exists on the Roles page.
                                const assignable = availableRoles.filter(
                                  (r) => r.name !== "Admin"
                                );
                                if (assignable.length === 0) {
                                  return (
                                    <SelectItem value="__no_roles__" disabled>
                                      No roles yet — create one on the Roles page
                                    </SelectItem>
                                  );
                                }
                                return assignable.map((r) => (
                                  <SelectItem key={r.role_id} value={r.name}>
                                    {r.name}
                                  </SelectItem>
                                ));
                              })()}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        {/* Branch */}
                        <div>
                          <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
                            Branch
                          </Label>
                          {branches.length > 0 ? (
                            <Select
                              value={formData.branch}
                              onValueChange={(v) => setFormData({ ...formData, branch: v })}
                            >
                              <SelectTrigger className="rounded-sm" data-testid="input-branch">
                                <SelectValue placeholder="Select branch" />
                              </SelectTrigger>
                              <SelectContent>
                                {branches.map((b) => (
                                  <SelectItem key={b.branch_id} value={b.location}>
                                    {b.location}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              value={formData.branch}
                              onChange={(e) => setFormData({ ...formData, branch: e.target.value })}
                              placeholder="Branch name"
                              className="rounded-sm"
                              data-testid="input-branch"
                            />
                          )}
                        </div>

                        {/* Status */}
                        <div>
                          <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
                            Status *
                          </Label>
                          <Select
                            value={formData.is_active ? "active" : "inactive"}
                            onValueChange={(v) =>
                              setFormData({ ...formData, is_active: v === "active" })
                            }
                          >
                            <SelectTrigger className="rounded-sm" data-testid="input-status">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="active">Active</SelectItem>
                              <SelectItem value="inactive">Inactive</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    {/* SECTION 2 — LOGIN */}
                    <div className="space-y-4" data-testid="user-form-login">
                      <h3 className="font-heading font-bold text-sm uppercase tracking-tight border-b border-gray-200 pb-2">
                        Login
                      </h3>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
                            Username {!editingUser && "*"}
                          </Label>
                          <Input
                            type="text"
                            value={formData.username}
                            onChange={(e) =>
                              setFormData({ ...formData, username: e.target.value })
                            }
                            placeholder="username"
                            className="rounded-sm"
                            disabled={!!editingUser && !isMaster}
                            data-testid="input-username"
                          />
                          {editingUser && !isMaster && (
                            <p className="text-[10px] text-gray-400 mt-1">
                              Username cannot be changed
                            </p>
                          )}
                        </div>

                        {!editingUser ? (
                          <div>
                            <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
                              Password *
                            </Label>
                            <Input
                              type="password"
                              value={formData.password}
                              onChange={(e) =>
                                setFormData({ ...formData, password: e.target.value })
                              }
                              placeholder="Password"
                              className="rounded-sm"
                              data-testid="input-password"
                            />
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </>
                );
              })()}

              <DialogFooter className="flex justify-between items-center pt-4 border-t">
                <div>
                  {editingUser && editingUser.is_active && editingUser.username !== "admin" && (
                    <Button
                      type="button"
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50 rounded-sm"
                      onClick={handleDeactivate}
                    >
                      Deactivate User
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-sm"
                    onClick={() => setDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-black text-white hover:bg-gray-800 rounded-sm"
                    disabled={savingUser}
                    data-testid="save-user-btn"
                  >
                    {editingUser ? "Update" : "Create"}
                  </Button>
                </div>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border border-gray-200 rounded-sm shadow-none">
          <CardContent className="p-4 text-center">
            <p className="font-heading font-black text-3xl" data-testid="kpi-active">
              {users.filter((u) => u.is_active).length}
            </p>
            <p className="text-xs uppercase tracking-wider text-gray-500">Active</p>
          </CardContent>
        </Card>
        <Card className="border border-gray-200 rounded-sm shadow-none">
          <CardContent className="p-4 text-center">
            <p className="font-heading font-black text-3xl" data-testid="kpi-inactive">
              {users.filter((u) => !u.is_active).length}
            </p>
            <p className="text-xs uppercase tracking-wider text-gray-500">Inactive</p>
          </CardContent>
        </Card>
        <Card className="border border-gray-200 rounded-sm shadow-none">
          <CardContent className="p-4 text-center">
            <p className="font-heading font-black text-3xl" data-testid="kpi-locked">
              {users.filter((u) => u.is_locked).length}
            </p>
            <p className="text-xs uppercase tracking-wider text-gray-500">Locked</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs and Users Table */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-4">
          <TabsTrigger value="active" className="rounded-sm">
            Active
          </TabsTrigger>
          <TabsTrigger value="inactive" className="rounded-sm">
            Inactive
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          <Card className="border border-gray-200 rounded-sm shadow-none overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="table-header bg-gray-50">
                  <TableHead className="text-xs">Name</TableHead>
                  <TableHead className="text-xs">Username</TableHead>
                  <TableHead className="text-xs">Role</TableHead>
                  <TableHead className="text-xs">Department</TableHead>
                  <TableHead className="text-xs">Designation</TableHead>
                  <TableHead className="text-xs w-32">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((u) => {
                  const isAdminRow = u.username === "admin";
                  return (
                  <TableRow
                    key={u.user_id}
                    className="hover:bg-gray-50"
                    data-testid={`user-row-${u.user_id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gray-200 rounded-sm flex items-center justify-center">
                          <span className="text-xs font-bold">{u.name?.[0] || "U"}</span>
                        </div>
                        <span className="font-medium">{u.name}</span>
                        {isAdminRow && (
                          <Badge
                            className="bg-black text-white rounded-sm text-[10px] uppercase tracking-wider"
                            data-testid={`system-badge-${u.user_id}`}
                          >
                            <Lock className="w-3 h-3 mr-1" strokeWidth={2} />
                            System
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{u.username}</TableCell>
                    <TableCell className="text-sm" data-testid={`role-cell-${u.user_id}`}>
                      {u.role || "-"}
                    </TableCell>
                    <TableCell className="text-sm">{u.department || "-"}</TableCell>
                    <TableCell className="text-sm">{u.designation || "-"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {/* Pencil is available on all rows including admin.
                            For the admin account, clicking it opens the
                            stripped form (Name/Phone/Username only) — see
                            `isAdminAccount` branch in the form. */}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 rounded-sm"
                          onClick={() => handleEdit(u)}
                          data-testid={`edit-${u.user_id}`}
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 rounded-sm"
                          onClick={() => {
                            setResetPasswordUser(u);
                            setResetPasswordDialogOpen(true);
                          }}
                          data-testid={`reset-password-${u.user_id}`}
                          title="Reset Password"
                        >
                          <Key className="w-4 h-4" strokeWidth={1.5} />
                        </Button>
                        
                        {/* Show Lock/Unlock only for Active users (except admin) */}
                        {u.is_active && !isAdminRow && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-sm"
                            onClick={() => handleToggleLock(u.user_id, u.is_locked)}
                            data-testid={`lock-${u.user_id}`}
                            title={u.is_locked ? "Unlock" : "Lock"}
                          >
                            {u.is_locked ? (
                              <Lock 
                                className="w-4 h-4 text-black" 
                                strokeWidth={2}
                                fill="currentColor"
                              />
                            ) : (
                              <Lock 
                                className="w-4 h-4 text-gray-400" 
                                strokeWidth={1.5}
                                fill="none"
                              />
                            )}
                          </Button>
                        )}

                        {/* Show Delete only for Inactive users */}
                        {!u.is_active && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-sm text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => {
                              setDeletingUser(u);
                              setDeleteDialogOpen(true);
                            }}
                            data-testid={`delete-${u.user_id}`}
                            title="Delete User"
                          >
                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      <DiscardConfirm
        open={discardConfirmOpen}
        onOpenChange={setDiscardConfirmOpen}
        onDiscard={() => {
          setDiscardConfirmOpen(false);
          setDialogOpen(false);
          resetForm();
        }}
      />

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent
          className="rounded-sm"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          closeClassName="p-1 transition-colors hover:bg-red-600 hover:text-white hover:opacity-100"
          onCloseClick={(e) => {
            if (newPassword) {
              e.preventDefault();
              setResetDiscardOpen(true);
            }
          }}
        >
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-xl tracking-tight uppercase">
              Reset Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-sm text-gray-600">
              Reset password for <strong>{resetPasswordUser?.name}</strong>
            </p>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
                New Password *
              </Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                className="rounded-sm"
              />
            </div>
          </div>
          <DialogFooter className="pt-4">
            <Button
              variant="outline"
              className="rounded-sm"
              onClick={() => {
                setResetPasswordDialogOpen(false);
                setResetPasswordUser(null);
                setNewPassword("");
              }}
            >
              Cancel
            </Button>
            <Button
              className="bg-black text-white hover:bg-gray-800 rounded-sm"
              onClick={handleResetPassword}
            >
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DiscardConfirm
        open={resetDiscardOpen}
        onOpenChange={setResetDiscardOpen}
        onDiscard={() => {
          setResetDiscardOpen(false);
          setResetPasswordDialogOpen(false);
          setResetPasswordUser(null);
          setNewPassword("");
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-xl tracking-tight uppercase">
              Delete User?
            </DialogTitle>
          </DialogHeader>
          <div className="pt-4">
            <p className="text-sm text-gray-600">
              This will permanently delete <strong>{deletingUser?.name}</strong>. This action cannot be undone.
            </p>
          </div>
          <DialogFooter className="pt-4">
            <Button
              variant="outline"
              className="rounded-sm"
              onClick={() => {
                setDeleteDialogOpen(false);
                setDeletingUser(null);
              }}
            >
              Cancel
            </Button>
            <Button
              className="bg-black text-white hover:bg-gray-800 rounded-sm"
              onClick={handleDelete}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
